import { ENVIRONMENT, LeagueConfig } from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
/**
 * @ts-ignore is used here due to @leagueplatform/legacy-locales being a
 * temporary measure that is served as a JS utility.
 */
// @ts-ignore
import { legacyStrings } from '@leagueplatform/legacy-locales';
import { cignaHealthTheme } from 'theme/cignaHealthTheme';
import EN_STRING_OVERRIDES from 'locales/en.json';
import { ASSETS_CONFIG } from 'common/asset-config';
import { APP_MANAGED_ROUTES } from 'common/constants';
import { LoadingSpinner } from 'components/loaders/loading-spinner.component';
import { ToastMessage } from 'components/toast-message/toast-message.component';
import { PolicyDetails } from 'components/policy-details/policy-details.component';
import { handleExternalLink } from 'utils/handle-external-link';

const {
  VITE_GA_PROPERTY_ID,
  VITE_SEGEMENT_KEY,
  VITE_WS_API_URL,
  VITE_API_URL,
  VITE_LEGACY_REST_API_URL,
  VITE_CONTENT_URL,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_APP_ENV,
  VITE_TENANT_ID,
  VITE_APP_ID,
} = import.meta.env;

const authConfig = {
  domain: VITE_AUTH0_DOMAIN!,
  client_id: VITE_AUTH0_CLIENT_ID!,
  redirect_uri: window.location.origin,
};

const analyticsConfig = {
  GAPropertyId: VITE_GA_PROPERTY_ID,
  segmentKey: VITE_SEGEMENT_KEY,
};

const apiConfig = {
  wsUrl: VITE_WS_API_URL!,
  url: VITE_API_URL!,
  legacyRestApi: VITE_LEGACY_REST_API_URL!,
};

export const config: LeagueConfig = {
  core: {
    clientId: VITE_APP_ID,
    tenantId: VITE_TENANT_ID,
    auth: {
      clientOptions: authConfig,
      idleSessionLifetimeHours: 0.166,
      sessionTimeoutOptions: {
        forceIdleSessionLogout: true,
      },
    },
    analytics: analyticsConfig,
    api: apiConfig,
    contentUrl: VITE_CONTENT_URL as string,
    appEnvironment: VITE_APP_ENV as ENVIRONMENT,
    routing: {
      adapter: createReactRouterAdapter(),
      handleExternalLink,
    },
    i18n: {
      strings: {
        default: { ...legacyStrings.default, ...EN_STRING_OVERRIDES },
        en: { ...legacyStrings.default, ...EN_STRING_OVERRIDES },
      },
    },
    ui: {
      theme: cignaHealthTheme,
      features: { pointsEnabled: true },
      components: {
        pageHeader: {
          shape: 'line',
          backgroundColor: 'surface.background.secondary',
        },
        toastMessage: { component: ToastMessage },
        loading: {
          element: <LoadingSpinner />,
        },
      },
    },
  },
  assets: {
    overrides: ASSETS_CONFIG,
  },
  healthProfile: {
    ratingsBarEnabled: true,
    careSdkEnabled: true,
    chatWithProfessionalEnabled: true,
  },
  careCollections: {
    showCareCollectionFeedbackComponent: true,
  },
  wallet: {
    showLandingPageHeaderBranding: true,
    enableNoNetworkLiveBalances: true,
    slots: {
      PolicyDetails,
    },
  },
  claims: {
    enableTabFilters: true,
    featureFlags: {
      shouldShowClaimDetails: true,
      shouldShowDownloadClaims: false,
    },
    supportLink: APP_MANAGED_ROUTES.CUSTOMER_SUPPORT,
    showTotalCostOverviewAndAdditionalResources: true,
    getTotalCostOverviewVisibilityForClaim: () => false,
    filterableProperties: {
      status: ['pending', 'approved', 'adjusted'],
      patientName: {
        isVisible: false,
      },
    },
    showClaimStatusDefinitions: false,
    showAdditionalResources: false,
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
  },
};
